import { ReactNode } from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface StatCardWithComparisonProps {
  icon: ReactNode;
  iconBgColor: string;
  label: string;
  value: string;
  valueColor?: string;
  change?: number;
  changePercent?: number;
  invertColors?: boolean; // Pour les dépenses, une baisse est positive
  noCard?: boolean; // Ne pas rendre le wrapper .card (utilisé quand le parent fournit déjà la card)
}

export default function StatCardWithComparison({
  icon,
  iconBgColor,
  label,
  value,
  valueColor = 'text-gray-900',
  change,
  changePercent,
  invertColors = false,
  noCard = false,
}: StatCardWithComparisonProps) {
  const { t } = useTranslation('dashboard');
  const hasComparison = change !== undefined && changePercent !== undefined;

  const isPositive = invertColors ? change! < 0 : change! > 0;
  const isNeutral = change === 0;

  const getChangeColor = () => {
    if (isNeutral) return 'text-gray-500';
    if (isPositive) return 'text-green-600';
    return 'text-red-600';
  };

  const getChangeBgColor = () => {
    if (isNeutral) return 'bg-gray-100';
    if (isPositive) return 'bg-green-100';
    return 'bg-red-100';
  };

  const ChangeIcon = isNeutral ? Minus : (isPositive ? TrendingUp : TrendingDown);

  const content = (
    <>
      <div className="flex items-center gap-4">
        <div className={clsx('p-3 rounded-lg', iconBgColor)}>
          {icon}
        </div>
        <div className="flex-1">
          <p className="text-sm text-gray-600">{label}</p>
          <p className={clsx('stat-card__value text-xl font-bold', valueColor)}>{value}</p>
        </div>
      </div>

      {hasComparison && (
        <div className={clsx('mt-3 pt-3 border-t border-gray-100 flex items-center gap-2')}>
          <div className={clsx('stat-card__delta flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium', getChangeBgColor(), getChangeColor())}>
            <ChangeIcon className="w-3 h-3" />
            <span>
              {changePercent! > 0 ? '+' : ''}
              {changePercent!.toFixed(1)}%
            </span>
          </div>
          <span className="text-xs text-gray-500">{t('stats.vsLastMonth')}</span>
        </div>
      )}
    </>
  );

  if (noCard) return content;
  return <div className="card">{content}</div>;
}
